import { useState } from 'react';
import type { AccountInfo } from '@azure/msal-browser';
import type { ICustomAuthPublicClientApplication } from '@azure/msal-browser/custom-auth';

import { beginEmailAuth, resendCode, submitCode, type CodeState } from './nativeAuth';
import { isAllowedSender } from './identity';
import { startSession } from './session';

/** Entra decides the code length at runtime - 8 today, 6 elsewhere. Read it, don't assume. */
function article(n: number): string {
    return n === 8 || n === 11 || n === 18 ? 'an' : 'a';
}

interface Props {
    client: ICustomAuthPublicClientApplication;
    allowedDomains: string[];
    /** Addresses granted access individually, outside the allowed domains. */
    allowedAddresses: string[];
    domainsSentence: string;
    onSignedIn: (account: AccountInfo | null) => void;
}

/**
 * Two steps and nothing else: the address, then the code.
 *
 * There is deliberately no mention of accounts, registering or signing up. A
 * first-time associate and a returning one see exactly the same two screens -
 * the sign-up/sign-in split is Entra's problem, handled in nativeAuth.
 */
export function SignInPanel({
    client,
    allowedDomains,
    allowedAddresses,
    domainsSentence,
    onSignedIn,
}: Props) {
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [codeState, setCodeState] = useState<CodeState | null>(null);
    const [codeLength, setCodeLength] = useState(6);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    // POC only: the untranslated failure, one tap away instead of via devtools.
    const [detail, setDetail] = useState<string | null>(null);

    const sendCode = async (address: string) => {
        setBusy(true);
        setError(null);
        setNotice(null);

        // Check the domain before involving Entra. Saves a pointless round trip
        // and, more importantly, avoids creating directory records for people
        // who were never going to be allowed in.
        if (!isAllowedSender(address, allowedDomains, allowedAddresses)) {
            setBusy(false);
            setError(`Use your Lowe's email address - ${domainsSentence}.`);
            return;
        }

        const result = await beginEmailAuth(client, address);
        setBusy(false);

        if (result.kind === 'error') {
            setError(result.message);
            return;
        }
        setCodeState(result.state);
        setCodeLength(result.codeLength || 6);
    };

    const verify = async () => {
        if (!codeState) return;
        setBusy(true);
        setError(null);
        setDetail(null);
        const outcome = await submitCode(codeState, code.trim());
        setBusy(false);

        if (!outcome.ok) {
            setError(outcome.message);
            setDetail(outcome.detail ?? null);
            return;
        }

        if (!outcome.idToken) {
            setError('We verified your code but could not start your session. Try again.');
            console.error('sign-in completed without an id token - session cannot be created');
            return;
        }

        const started = await startSession(outcome.idToken);
        if (started === 'forbidden') {
            setError(`Use your Lowe's email address - ${domainsSentence}.`);
            return;
        }
        if (started === 'failed') {
            setError('We verified your code but could not start your session. Try again.');
            return;
        }
        onSignedIn(outcome.account);
    };

    const again = async () => {
        if (!codeState) return;
        setBusy(true);
        setError(null);
        const outcome = await resendCode(codeState);
        setBusy(false);

        if (!outcome.ok) {
            setError(outcome.message);
            return;
        }
        // Adopt the new state. The fresh code belongs to it, not to the old one.
        setCodeState(outcome.state);
        setCodeLength(outcome.codeLength || codeLength);
        setCode('');
        setNotice('We sent another code. Use the newest one.');
    };

    const startOver = () => {
        setCodeState(null);
        setCode('');
        setError(null);
        setNotice(null);
    };

    if (codeState) {
        return (
            <div className="signin">
                <h2>Check your email</h2>
                <p>
                    We sent {article(codeLength)} {codeLength}-digit code to{' '}
                    <strong>{email}</strong>.
                </p>

                <label className="field">
                    <span className="field-label">Code</span>
                    <input
                        className="control code-input"
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={codeLength}
                        // Autofocus is right here: there is one field and the
                        // associate arrived expecting to type in it.
                        autoFocus
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && code.length === codeLength) void verify();
                        }}
                    />
                </label>

                {notice && <p className="hint">{notice}</p>}
                {error && (
                    <div className="alert alert-error">
                        <p style={{ margin: 0 }}>{error}</p>
                        {detail && (
                            <details className="detail">
                                <summary>Technical details</summary>
                                <code>{detail}</code>
                            </details>
                        )}
                    </div>
                )}

                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => void verify()}
                    disabled={busy || code.length !== codeLength}
                >
                    {busy ? 'Checking...' : 'Sign in'}
                </button>

                <div className="link-row">
                    <button type="button" className="linkish" onClick={() => void again()} disabled={busy}>
                        Send a new code
                    </button>
                    <button type="button" className="linkish" onClick={startOver} disabled={busy}>
                        Use a different email
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="signin">
            <h2>Report plant loss</h2>
            <p>Enter your Lowe&rsquo;s email and we&rsquo;ll send you a code.</p>

            <label className="field">
                <span className="field-label">Email address</span>
                <input
                    className="control"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    inputMode="email"
                    autoComplete="email"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder={`name@${allowedDomains[0] ?? 'lowes.com'}`}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && email.trim()) void sendCode(email.trim());
                    }}
                />
            </label>

            {error && (
                <div className="alert alert-error">
                    <p style={{ margin: 0 }}>{error}</p>
                </div>
            )}

            <button
                type="button"
                className="btn btn-primary"
                onClick={() => void sendCode(email.trim())}
                disabled={busy || email.trim() === ''}
            >
                {busy ? 'Sending...' : 'Send code'}
            </button>

            <p className="hint">
                You stay signed in on this device, so you will not need a code next time.
            </p>
        </div>
    );
}
